import { useEffect, useState } from "react";
import { useAsync } from "react-use";
import { fetchExplorer, fetchRoute, fetchItineraryTeaser, fetchVanillaItinerary } from "../../api";
import { getDaysCount, getDistance } from "../itinerary/utils";

const defaultRoutes = { routesIds: [], routesById: {}, loading: false, error: null, totalCount: 0 };
export const usePagedRoutes = (start, limit, tags) => {
    const [routes, setRoutes] = useState(defaultRoutes);
    const [, setAbortController] = useState();
    useEffect(() => {
        const callback = async () => {
            setRoutes(prevRoutes => ({
                ...prevRoutes,
                loading: true,
                error: null,
            }));
            window.scrollTo({
                left: 0,
                top: 0,
            });
            const _abortController = new AbortController();
            setAbortController(abortController => {
                if (abortController) {
                    abortController.abort();
                }
                return _abortController;
            });
            const { routeIds: routesIds, totalResults: totalCount, ..._routes } = await fetchExplorer(
                start,
                limit,
                tags,
                undefined,
                _abortController.signal,
            );
            setRoutes({
                ..._routes,
                routesIds,
                totalCount,
                loading: false,
            });
        };
        callback();
    }, [start, limit, tags]);
    return routes;
};

const defaultRouteData = { loading: false, error: null, route: null };
export const useSingleRoute = id => {
    const [data, setData] = useState(defaultRouteData);
    const [, setAbortController] = useState();
    useEffect(() => {
        const callback = async () => {
            setData(prevData => ({
                ...prevData,
                loading: true,
                error: null,
            }));
            window.scrollTo({
                left: 0,
                top: 0,
            });
            const newAbortController = new AbortController();
            setAbortController(abortController => {
                if (abortController) {
                    abortController.abort();
                }
                return newAbortController;
            });
            const { data: route, error } = await fetchRoute(id, newAbortController.signal);
            setData({
                route,
                error,
                loading: false,
            });
        };
        callback();
    }, [id]);
    return data;
};

/**
 * Fetch itineraries and calculate their respective days and distance
 * @param {string[]} itineraryIds - List of itinerary ids
 * @param {boolean} isDisabled - Wait until isDisabledis true to initialize fetch/requests
 * @returns {[object[], boolean]} Itineraries + calculated days and distance and isLoading
 */
export const useItineraryTeasers = (itineraryIds, isDisabled = false) => {
    const [itineraries, setItineraries] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useAsync(async () => {
        try {
            if (isDisabled) {
                return;
            }
            if (!itineraryIds) {
                return;
            }
            setIsLoading(true);
            const itineraryTeasers = await Promise.all(
                itineraryIds.map(itineraryId => fetchItineraryTeaser(itineraryId)),
            );
            if (itineraryTeasers.length) {
                const newItineraries = itineraryTeasers
                    .map((item, index) => {
                        if (!item) {
                            return null;
                        }
                        return {
                            ...item,
                            id: itineraryIds[index],
                            distance: 0,
                            days: 0,
                        };
                    })
                    .filter(Boolean);
                setItineraries(newItineraries);
            }
            setIsLoading(false);
            const itineraries = await Promise.all(itineraryIds.map(itineraryId => fetchVanillaItinerary(itineraryId)));
            if (itineraries.length) {
                const newItineraries = itineraryTeasers
                    .map((itineraryTeaser, index) => {
                        if (!itineraryTeaser) {
                            return null;
                        }
                        const itineraryData = itineraries[index][0]?.itineraryData?.[0];
                        const days = getDaysCount(itineraryData);
                        const id = itineraryIds[index];
                        const distance = getDistance(itineraryData);

                        return {
                            ...itineraryTeaser,
                            id,
                            distance,
                            days,
                        };
                    })
                    .filter(Boolean);
                setItineraries(newItineraries);
            }
        } catch (error) {
            console.warn(error);
        }
    }, [itineraryIds, isDisabled]);

    return [itineraries, isLoading];
};
